/**
 * Supplier Upload History — the customer-side tracker for the monthly upload
 * cycle. Which suppliers have sent their Supplier Performance and Quality
 * files for a period, which have not, and a way to chase the latter.
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Download, Search, AlertCircle, RefreshCw, Loader2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import type { SupplierUploadDatasetType, SupplierUploadTracking } from '../../lib/api/barratt';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { DATASET_LABELS, DATASET_ORDER, periodKeyLabel } from './uploadCycle';
import { useUploadHistory, type UploadTab } from './upload-history/useUploadHistory';
import UploadKpiTiles from './upload-history/UploadKpiTiles';
import UploadedTable from './upload-history/UploadedTable';
import NotUploadedTable from './upload-history/NotUploadedTable';
import { exportUploadHistoryCsv } from './upload-history/uploadHistoryCsv';

type DatasetFilter = 'all' | SupplierUploadDatasetType;

/**
 * Suppliers behind a set of rows — a supplier owing both files is one supplier.
 *
 * The tabs and tiles say "suppliers", so they count suppliers; the tables below
 * stay one row per file, and their captions say so.
 */
function supplierCount(rows: SupplierUploadTracking[]): number {
  return new Set(rows.map((row) => row.supplier)).size;
}

/** '436 files still outstanding' — the caption counts files, and says so. */
function fileLine(files: number, verb: string): string {
  return `${files.toLocaleString()} file${files === 1 ? '' : 's'} ${verb}`;
}

function applyFilters(rows: SupplierUploadTracking[], dataset: DatasetFilter, search: string): SupplierUploadTracking[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (dataset !== 'all' && row.dataset_type !== dataset) return false;
    if (needle && !(row.supplier_name ?? '').toLowerCase().includes(needle)) return false;
    return true;
  });
}

export default function SupplierUploadHistory() {
  const confirm = useConfirm();
  const history = useUploadHistory();
  const [tab, setTab] = useState<UploadTab>('uploaded');
  const [dataset, setDataset] = useState<DatasetFilter>('all');
  const [search, setSearch] = useState('');

  const periodLabel =
    history.summary?.period_label
    ?? history.periods.find((p) => p.period === history.period)?.label
    ?? (history.period ? periodKeyLabel(history.period) : '');

  const uploadedRows = useMemo(
    () => applyFilters(history.uploaded, dataset, search),
    [history.uploaded, dataset, search],
  );
  const notUploadedRows = useMemo(
    () => applyFilters(history.notUploaded, dataset, search),
    [history.notUploaded, dataset, search],
  );
  const filtered = dataset !== 'all' || search.trim() !== '';

  const emptyFor = (kind: UploadTab): string => {
    if (filtered) return search.trim() ? `No suppliers match "${search.trim()}".` : 'No suppliers match the selected dataset.';
    return kind === 'uploaded'
      ? `No suppliers uploaded for ${periodLabel} yet.`
      : `Every supplier has uploaded for ${periodLabel}.`;
  };

  const handleExport = () => {
    const rows = tab === 'uploaded' ? uploadedRows : notUploadedRows;
    if (rows.length === 0) {
      toast.info('Nothing to export for this tab.');
      return;
    }
    exportUploadHistoryCsv(tab, history.period, rows);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`);
  };

  // A dataset filter or search narrows "all" to what is on screen; otherwise
  // the backend chases every not-uploaded row in the period.
  const handleChaseAll = async () => {
    const count = notUploadedRows.length;
    if (count === 0) return;
    const ok = await confirm({
      title: 'Chase all suppliers',
      description: `Send a reminder email to ${count} supplier upload${count === 1 ? '' : 's'} still outstanding for ${periodLabel}? Suppliers without a contact email are skipped.`,
      confirmLabel: 'Send reminders',
    });
    if (!ok) return;
    await history.chaseAll(filtered ? notUploadedRows : undefined);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Supplier Upload History</h1>
          <p className="text-muted-foreground">
            Who has sent their monthly Supplier Performance and Quality files, and who still owes them.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={history.loading || !!history.error}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Select value={history.period} onValueChange={history.setPeriod} disabled={history.periods.length === 0}>
              <SelectTrigger className="w-full md:w-44" aria-label="Period" data-testid="period-select">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                {history.periods.map((p) => (
                  <SelectItem key={p.period} value={p.period}>
                    {p.label}{p.is_current ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dataset} onValueChange={(v) => setDataset(v as DatasetFilter)}>
              <SelectTrigger className="w-full md:w-52" aria-label="Dataset" data-testid="dataset-select">
                <SelectValue placeholder="All datasets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All datasets</SelectItem>
                {DATASET_ORDER.map((key) => (
                  <SelectItem key={key} value={key}>{DATASET_LABELS[key]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search suppliers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search suppliers"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {history.error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-sm font-medium">Could not load upload history</p>
            <p className="max-w-md text-sm text-muted-foreground">{history.error}</p>
            <Button variant="outline" size="sm" onClick={history.retry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <UploadKpiTiles summary={history.summary} loading={history.loading} />

          <Tabs value={tab} onValueChange={(v) => setTab(v as UploadTab)} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="uploaded">
                Suppliers did upload{!history.loading && ` (${supplierCount(uploadedRows)})`}
              </TabsTrigger>
              <TabsTrigger value="not-uploaded">
                Suppliers did not upload{!history.loading && ` (${supplierCount(notUploadedRows)})`}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="uploaded" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-700">Suppliers who uploaded</CardTitle>
                  <CardDescription>
                    {fileLine(uploadedRows.length, 'received')} for {periodLabel || 'the selected period'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <UploadedTable rows={uploadedRows} loading={history.loading} emptyMessage={emptyFor('uploaded')} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="not-uploaded" className="mt-6">
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-red-700">Suppliers who did not upload</CardTitle>
                    <CardDescription>
                      {fileLine(notUploadedRows.length, 'still outstanding')} for{' '}
                      {periodLabel || 'the selected period'}
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => void handleChaseAll()}
                    disabled={history.loading || history.chasingAll || notUploadedRows.length === 0}
                    data-testid="chase-all"
                  >
                    {history.chasingAll ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Megaphone className="h-4 w-4 mr-2" />
                    )}
                    Chase all
                  </Button>
                </CardHeader>
                <CardContent>
                  <NotUploadedTable
                    rows={notUploadedRows}
                    loading={history.loading}
                    emptyMessage={emptyFor('not-uploaded')}
                    onChase={(row) => void history.chase(row)}
                    chasingIds={history.chasingIds}
                    chasingAll={history.chasingAll}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
